import { FC, useState } from 'react';
import { DayPicker, type DateRange } from 'react-day-picker';
import { ptBR } from 'react-day-picker/locale';
import 'react-day-picker/style.css';
import { dataParaIso, isoParaData } from '@/utils/filtroPaineis';
import { fmtData } from '@/utils/painelFormat';
import './calendario.css';

interface Props {
  /** Período em vigor (ISO), para o calendário abrir nele. */
  inicio: string | null;
  fim: string | null;
  /** Dias que os dados cobrem: fora disso o dia fica desabilitado. */
  janela: { inicio: string; fim: string } | null;
  onAplicar: (inicio: string, fim: string) => void;
  onCancelar: () => void;
}

/**
 * Escolha do período personalizado num calendário só: o primeiro clique marca o
 * início, o segundo o fim. Nada muda na tela até "Aplicar" — cada mudança remonta a
 * aba no servidor.
 */
const CalendarioPeriodo: FC<Props> = ({ inicio, fim, janela, onAplicar, onCancelar }) => {
  const [faixa, setFaixa] = useState<DateRange | undefined>(
    inicio && fim ? { from: isoParaData(inicio), to: isoParaData(fim) } : undefined,
  );
  const limites = janela ? { inicio: isoParaData(janela.inicio), fim: isoParaData(janela.fim) } : null;
  const completo = !!(faixa?.from && faixa?.to);
  // Sem período escolhido, abre no mês anterior e no atual (dois meses na tela).
  const hoje = new Date();
  const abertura = faixa?.from ?? new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);

  return (
    <div className="calendario-paineis flex flex-col gap-3" data-testid="filtro-calendario">
      <DayPicker
        mode="range"
        locale={ptBR}
        numberOfMonths={2}
        selected={faixa}
        onSelect={setFaixa}
        defaultMonth={abertura}
        startMonth={limites?.inicio}
        endMonth={limites?.fim}
        disabled={limites ? [{ before: limites.inicio }, { after: limites.fim }] : undefined}
      />
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-theme-border pt-3">
        <p className="text-sm text-content-secondary" aria-live="polite">
          {faixa?.from ? fmtData(dataParaIso(faixa.from)) : 'Escolha o início'}
          {' a '}
          {faixa?.to ? fmtData(dataParaIso(faixa.to)) : '…'}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancelar}
            className="rounded-lg border border-theme-border px-3 py-1.5 text-sm text-content-secondary hover:text-content-primary"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!completo}
            onClick={() => faixa?.from && faixa.to && onAplicar(dataParaIso(faixa.from), dataParaIso(faixa.to))}
            className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarioPeriodo;
